import React from "react";
import { useTranslation } from "react-i18next";
import {
  useRetrieveCalendarsHolidays,
  useRetrieveDefaultCalendarHolidays,
  useAssignCalendarHolidaysToPlanzone,
} from "@planzone/view-models";
import { Identifiable, CalendarHolidays as Calendar } from "@planzone/dto";
import { UseCases } from "@planzone/core";
import { useModal } from "@root/client/modal";
import { EventIcon } from "@common/eventIcon";
import { Props } from "./types";
import { CalendarHolidaysSelection } from "./defaultCalendarHolidays";
import { CalendarHolidays } from "./calendarHolidays";
import styles from "./styles.scss";

function CalendarsHolidays({ planzone }: Props) {
  const { t } = useTranslation();
  const { pushView } = useModal();
  const {
    isRequestPending: isCalendarsHolidaysRequestPending,
    isRequestSuccess: isCalendarsHolidaysRequestSuccess,
    calendarsHolidays,
    retrieveCalendarHolidays,
  } = useRetrieveCalendarsHolidays();
  const { defaultCalendarHolidays, retrieveDefaultCalendarHolidays } =
    useRetrieveDefaultCalendarHolidays(planzone);
  const {
    isRequestSuccess: isAssignDefaultCalendarHolidaysRequestSuccess,
    assignDefaultCalendarHolidaysToPlanzone,
  } = useAssignCalendarHolidaysToPlanzone(planzone);

  React.useEffect(() => {
    retrieveCalendarHolidays(planzone);
    retrieveDefaultCalendarHolidays(planzone);
  }, [planzone]);

  React.useEffect(() => {
    if (isAssignDefaultCalendarHolidaysRequestSuccess) {
      retrieveDefaultCalendarHolidays(planzone);
    }
  }, [isAssignDefaultCalendarHolidaysRequestSuccess, planzone]);

  const handleSelectCalendar = (calendar: Identifiable<Calendar>) => {
    assignDefaultCalendarHolidaysToPlanzone({
      planzone,
      defaultCalendarHolidays: calendar,
    });
  };

  const isReady =
    !isCalendarsHolidaysRequestPending && isCalendarsHolidaysRequestSuccess;

  return (
    <div className={styles.container}>
      <div className={styles.block}>
        <p className={styles.label}>
          {t("planzoneParametersPage.calendarHolidays.subTitleDefaultCalendar")}
        </p>
        <p className={styles.hint}>
          {t("planzoneParametersPage.calendarHolidays.defaultHint")}
        </p>
        {isReady && (
          <CalendarHolidaysSelection
            placeholder={t(
              "planzoneParametersPage.calendarHolidays.placeholder",
            )}
            selectedCalendarHolidays={defaultCalendarHolidays}
            calendarHolidaysList={calendarsHolidays}
            select={handleSelectCalendar}
          />
        )}
      </div>

      <div className={styles.block}>
        <div className={styles.blockHead}>
          <p className={styles.label}>
            {t("planzoneParametersPage.calendarHolidays.subTitle")}
          </p>
          {isReady && calendarsHolidays.length > 0 && (
            <span className={styles.count}>{calendarsHolidays.length}</span>
          )}
        </div>

        {isReady && calendarsHolidays.length === 0 && (
          <div className={styles.empty}>
            <EventIcon className={styles.emptyIcon} />
            {t("planzoneParametersPage.calendarHolidays.emptyState")}
          </div>
        )}

        {isReady && calendarsHolidays.length > 0 && (
          <div className={styles.list}>
            {calendarsHolidays.map((calendar) => (
              <CalendarHolidays
                key={calendar.uuid}
                calendarHolidays={calendar}
                isDefault={calendar.uuid === defaultCalendarHolidays?.uuid}
              />
            ))}
          </div>
        )}

        <button
          className={styles.addButton}
          onClick={() =>
            pushView({
              key: UseCases.createCalendarHolidays,
              data: planzone,
            })
          }
        >
          <span className={styles.addPlus}>+</span>
          {t("planzoneParametersPage.calendarHolidays.submit")}
        </button>
      </div>
    </div>
  );
}

export { CalendarsHolidays };
